import { useState, useEffect } from 'react'
import { Eye, Search, UserCheck, AlertTriangle, X } from 'lucide-react'
import { adminService } from '@/services/adminService'
import { useImpersonation } from '@/contexts/ImpersonationContext'

interface EligibleUser {
  id: string
  name: string
  email: string
  role: string
  status: string
}

export function AdminImpersonation() {
  const { isImpersonating, targetUser, originalAdmin, startImpersonation, endImpersonation } =
    useImpersonation()

  const [users, setUsers] = useState<EligibleUser[]>([])
  const [filteredUsers, setFilteredUsers] = useState<EligibleUser[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [startingUserId, setStartingUserId] = useState<string | null>(null)

  // Fetch eligible users (business_owner, lender, analyst — NOT admin)
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await adminService.listUsers({
          pageSize: 200,
          page: 1,
        })
        const allUsers: EligibleUser[] = (response.data?.items || response.data || []).filter(
          (u: EligibleUser) =>
            ['business_owner', 'lender', 'analyst'].includes(u.role)
        )
        setUsers(allUsers)
        setFilteredUsers(allUsers)
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load users')
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [])

  // Filter users by search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users)
      return
    }
    const term = searchTerm.toLowerCase()
    setFilteredUsers(
      users.filter(
        (u) =>
          u.name.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          u.role.toLowerCase().includes(term)
      )
    )
  }, [searchTerm, users])

  const handleStartImpersonation = async (userId: string) => {
    setStartingUserId(userId)
    setError(null)
    try {
      await startImpersonation(userId)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to start impersonation')
    } finally {
      setStartingUserId(null)
    }
  }

  const handleEndImpersonation = async () => {
    setError(null)
    try {
      await endImpersonation()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to end impersonation')
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'business_owner':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'lender':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'analyst':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          User Impersonation
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          View the platform as another user to debug issues and verify their experience. All
          write operations are blocked during impersonation.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Active session info */}
      {isImpersonating && targetUser && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-200 dark:bg-orange-800">
                <Eye className="h-5 w-5 text-orange-700 dark:text-orange-300" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                  Active Impersonation Session
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Viewing as <span className="font-medium">{targetUser.name}</span> (
                  {targetUser.email}) — Role: {targetUser.role}
                </p>
                {originalAdmin && (
                  <p className="mt-0.5 text-xs text-orange-600 dark:text-orange-400">
                    Original admin: {originalAdmin.name} ({originalAdmin.email})
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleEndImpersonation}
              className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
              Exit Impersonation
            </button>
          </div>
        </div>
      )}

      {/* User search/selection interface */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Select User to Impersonate
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Only users with business_owner, lender, or analyst roles are eligible.
        </p>

        {/* Search input */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 dark:focus:border-blue-400 dark:focus:ring-blue-400"
          />
        </div>

        {/* User list */}
        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                Loading users...
              </span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? 'No users match your search.' : 'No eligible users found.'}
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user.email}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(user.role)}`}
                    >
                      {user.role.replace('_', ' ')}
                    </span>
                  </div>
                  <button
                    onClick={() => handleStartImpersonation(user.id)}
                    disabled={isImpersonating || startingUserId === user.id}
                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800"
                  >
                    {startingUserId === user.id ? (
                      <>
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <UserCheck className="h-3.5 w-3.5" />
                        Start Impersonation
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
